// Firebase Configuration
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import { getFirestore, doc, setDoc, getDoc, onSnapshot, collection, addDoc, arrayUnion, updateDoc, increment, getDocs, writeBatch, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { getDatabase, ref, onValue, onDisconnect, set, serverTimestamp as rtdbServerTimestamp } from 'firebase/database';


const firebaseConfig = {
  apiKey: "AIzaSyA8EycKGisYYZbFnmImI-gqthtx9dJPmcE",
  authDomain: "investport-pro.firebaseapp.com",
  databaseURL: "https://investport-pro-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "investport-pro",
  storageBucket: "investport-pro.firebasestorage.app",
  messagingSenderId: "654821287847",
  appId: "1:654821287847:web:b0df2e456edc1c964eb882",
  measurementId: "G-74T40RQGH0"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Analytics (optional)
let analytics = null;
if (typeof window !== 'undefined') {
  analytics = getAnalytics(app);
}

// Initialize Firebase Authentication
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Initialize Firestore
export const db = getFirestore(app);

// Initialize Realtime Database
export const rtdb = getDatabase(app);

// Presence System
export const setupPresence = (user) => {
  if (!user || !rtdb) return;
  const uid = user.uid;
  const userStatusDatabaseRef = ref(rtdb, `/status/${uid}`);
  const connectedRef = ref(rtdb, '.info/connected');

  let heartbeatInterval = null;

  onValue(connectedRef, (snapshot) => {
    if (snapshot.val() === false) {
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      return;
    }

    onDisconnect(userStatusDatabaseRef).set({
      state: 'offline',
      last_changed: rtdbServerTimestamp(),
      email: user.email || ''
    }).then(() => {
      set(userStatusDatabaseRef, {
        state: 'online',
        last_changed: rtdbServerTimestamp(),
        email: user.email || ''
      });

      // Heartbeat ping every 2 minutes while active
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      heartbeatInterval = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          set(userStatusDatabaseRef, {
            state: 'online',
            last_changed: rtdbServerTimestamp(),
            email: user.email || ''
          }).catch(() => {});
        }
      }, 2 * 60 * 1000);
    });
  });

  // Set offline status when app is backgrounded or tab closed
  if (typeof window !== 'undefined') {
    const handleVisibilityOrUnload = () => {
      if (document.visibilityState === 'hidden') {
        set(userStatusDatabaseRef, {
          state: 'offline',
          last_changed: rtdbServerTimestamp(),
          email: user.email || ''
        }).catch(() => {});
      } else if (document.visibilityState === 'visible') {
        set(userStatusDatabaseRef, {
          state: 'online',
          last_changed: rtdbServerTimestamp(),
          email: user.email || ''
        }).catch(() => {});
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityOrUnload);
    window.addEventListener('beforeunload', handleVisibilityOrUnload);
  }
};

// ⛔ DEPRECATED — DO NOT USE.
// Transactions now live exclusively in the per-document subcollection
// users/{uid}/transactions (see writeTransactionChanges). Writing the whole
// `transactions` array back to users/{uid} caused cross-device clobbering and
// is permanently disabled. This stub is kept only so any stale reference fails
// loudly instead of silently corrupting data.
export const saveUserData = async () => {
  console.error('[saveUserData] DISABLED: transactions are stored in the subcollection. Use writeTransactionChanges instead.');
  return false;
};

// Save only supportLevels (completely overwrite the field to allow deletions, fallback to setDoc if new)
export const saveSupportLevels = async (userId, supportLevels) => {
  try {
    const docRef = doc(db, 'users', userId);
    try {
      // updateDoc completely replaces the field, preventing deep-merge from keeping deleted keys
      await updateDoc(docRef, {
        supportLevels,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      // If doc doesn't exist yet, fallback to setDoc
      await setDoc(docRef, {
        supportLevels,
        updatedAt: new Date().toISOString()
      }, { merge: true });
    }
    return true;
  } catch (error) {
    console.error('Error saving support levels:', error);
    return false;
  }
};

// Save only allocationLimits (per-symbol max weight %). Overwrites the field to
// allow deletions, mirroring saveSupportLevels.
export const saveAllocationLimits = async (userId, allocationLimits) => {
  try {
    const docRef = doc(db, 'users', userId);
    try {
      await updateDoc(docRef, {
        allocationLimits,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      await setDoc(docRef, {
        allocationLimits,
        updatedAt: new Date().toISOString()
      }, { merge: true });
    }
    return true;
  } catch (error) {
    console.error('Error saving allocation limits:', error);
    return false;
  }
};

export const saveTelegramConfig = async (userId, telegramConfig) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      telegramConfig,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error saving telegram config:', error);
    return false;
  }
};

export const saveCostBasisMethod = async (userId, costBasisMethod) => {
  try {
    const docRef = doc(db, 'users', userId);
    try {
      await updateDoc(docRef, {
        costBasisMethod,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      await setDoc(docRef, {
        costBasisMethod,
        updatedAt: new Date().toISOString()
      }, { merge: true });
    }
    return true;
  } catch (error) {
    console.error('Error saving cost basis method:', error);
    return false;
  }
};

export const saveWatchlists = async (userId, watchlists) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      watchlists,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error saving watchlists:', error);
    return false;
  }
};

// Persist a compact, admin-facing portfolio summary on the user doc.
// This is a denormalized read model for the Admin panel ONLY — it is NOT the
// source of truth (the transactions subcollection is). Stored as a small map so
// the admin dashboard never has to read every user's transactions subcollection.
export const saveAdminSummary = async (userId, adminSummary) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      adminSummary,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error saving admin summary:', error);
    return false;
  }
};

export const getUserData = async (userId, retries = 3) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data();
      }
      return null;
    } catch (error) {
      console.error(`Error getting data (attempt ${attempt}/${retries}):`, error);
      if (attempt === retries) throw error;
      // Wait before retry (exponential backoff: 1s, 2s, 4s)
      await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
    }
  }
};

export const subscribeToUserData = (userId, callback) => {
  const docRef = doc(db, 'users', userId);
  return onSnapshot(docRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data());
    }
  });
};

// ─────────────────────────────────────────────────────────────────
// Transactions subcollection — real-time, per-document sync.
// Each transaction is its own document at users/{uid}/transactions/{txId}.
// This eliminates whole-array overwrites, so concurrent edits from
// multiple devices never clobber each other (no "revert" race).
// ─────────────────────────────────────────────────────────────────
const txCollection = (userId) => collection(db, 'users', userId, 'transactions');

// Strip Firestore-only metadata before handing data to the app.
const cleanTx = (data, id) => {
  const { _serverUpdatedAt, ...rest } = data || {};
  return { ...rest, id };
};

// Firestore rejects documents that contain `undefined` field values — and in a
// batch a single bad field fails ALL writes. Drop undefined keys defensively.
const stripUndefined = (obj) => {
  const out = {};
  for (const key in obj) {
    if (obj[key] !== undefined) out[key] = obj[key];
  }
  return out;
};

export const subscribeToTransactions = (userId, callback, onError) => {
  return onSnapshot(
    txCollection(userId),
    (snap) => {
      const txs = [];
      snap.forEach((d) => txs.push(cleanTx(d.data(), d.id)));
      callback(txs);
    },
    (err) => {
      console.error('subscribeToTransactions error:', err);
      if (onError) onError(err);
    }
  );
};

export const getTransactionsOnce = async (userId) => {
  const snap = await getDocs(txCollection(userId));
  const txs = [];
  snap.forEach((d) => txs.push(cleanTx(d.data(), d.id)));
  return txs;
};

// Apply a set of granular changes in one (chunked) batch.
// upserts: array of full transaction objects (must each have an `id`)
// deletes: array of transaction ids to remove
export const writeTransactionChanges = async (userId, upserts = [], deletes = []) => {
  const ups = Array.isArray(upserts) ? upserts.filter((t) => t && t.id) : [];
  const dels = Array.isArray(deletes) ? deletes.filter(Boolean) : [];
  if (ups.length === 0 && dels.length === 0) return true;
  try {
    const ops = [
      ...ups.map((tx) => ({ kind: 'set', tx })),
      ...dels.map((id) => ({ kind: 'del', id })),
    ];
    // Firestore batch limit is 500 — chunk to stay safely under it.
    for (let i = 0; i < ops.length; i += 450) {
      const batch = writeBatch(db);
      for (const op of ops.slice(i, i + 450)) {
        if (op.kind === 'set') {
          const { id, _serverUpdatedAt, ...rest } = op.tx;
          batch.set(doc(db, 'users', userId, 'transactions', String(id)), {
            ...stripUndefined(rest),
            updatedAt: rest.updatedAt || new Date().toISOString(),
            _serverUpdatedAt: serverTimestamp(),
          });
        } else {
          batch.delete(doc(db, 'users', userId, 'transactions', String(op.id)));
        }
      }
      await batch.commit();
    }
    return true;
  } catch (error) {
    console.error('writeTransactionChanges error:', error);
    return false;
  }
};

// One-time migration: copy a legacy transactions array into the subcollection
// the first time (only when the subcollection is still empty).
export const migrateTransactionsToSubcollection = async (userId, legacyTxs) => {
  try {
    const existing = await getDocs(txCollection(userId));
    if (!existing.empty) return { migrated: false, count: existing.size };
    if (!Array.isArray(legacyTxs) || legacyTxs.length === 0) return { migrated: false, count: 0 };
    await writeTransactionChanges(userId, legacyTxs, []);
    return { migrated: true, count: legacyTxs.length };
  } catch (error) {
    console.error('migrateTransactionsToSubcollection error:', error);
    return { migrated: false, count: 0, error };
  }
};

export const getGlobalMarketPrices = async () => {
  try {
    const docRef = doc(db, 'global_state', 'market_prices');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting global prices:', error);
    return null;
  }
};

export const subscribeToGlobalMarketPrices = (callback) => {
  const docRef = doc(db, 'global_state', 'market_prices');
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data());
    }
  }, (error) => {
    console.error("Error subscribing to market prices: ", error);
  });
};

/**
 * Subscribe to technical indicators (RSI, Support/Resistance, MA, 52W H/L)
 * Updated hourly by the updateTechnicalIndicators cloud function.
 * Returns a single doc with shape: { AAPL: {...}, MSFT: {...}, _updatedAt }
 */
export const subscribeToTechnicals = (callback) => {
  const docRef = doc(db, 'global_state', 'technicals');
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data());
    } else {
      callback({});
    }
  }, (error) => {
    console.error("Error subscribing to technicals: ", error);
  });
};

export const updateGlobalMarketPrices = async (pricesObj) => {
  try {
    if (!pricesObj || Object.keys(pricesObj).length === 0) return false;
    
    // Add updated timestamp for tracking age
    const payload = {
      ...pricesObj,
      _lastUpdated: new Date().getTime()
    };
    
    await setDoc(doc(db, 'global_state', 'market_prices'), payload, { merge: true });
    return true;
  } catch (error) {
    console.error('Error updating global prices:', error);
    return false;
  }
};

export const registerActiveSymbols = async (symbolsArray) => {
  try {
    if (!symbolsArray || symbolsArray.length === 0) return;
    const docRef = doc(db, 'global_state', 'active_symbols');
    
    // Use arrayUnion directly for robust, race-condition-free updates
    await setDoc(docRef, {
      symbols: arrayUnion(...symbolsArray),
      updatedAt: Date.now()
    }, { merge: true });
  } catch (err) {
    console.error("Failed to register active symbols", err);
  }
};

/**
 * On-demand fetch for dividend & earnings data of a new symbol.
 * Calls the backend fetchSymbolData endpoint which checks if data already exists
 * in the calendars before making any API calls (0 credits if data exists).
 * Fire-and-forget — failures are silently logged.
 */
export const fetchSymbolDataOnDemand = async (symbol) => {
  try {
    if (!symbol) return;
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    if (!auth.currentUser) return;

    const token = await auth.currentUser.getIdToken();
    const res = await fetch('https://us-central1-investport-pro.cloudfunctions.net/fetchSymbolData', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ symbol }),
    });
    const data = await res.json();
    if (data.dividendsFetched || data.earningsFetched) {
      console.log(`📥 On-demand data fetched for ${symbol}:`, data.message);
    }
  } catch (err) {
    // Silent — on-demand fetch must never break the app
    console.warn('fetchSymbolDataOnDemand error:', err.message);
  }
};

/**
 * Force an IMMEDIATE price refresh for the given symbols (e.g. on pull-to-refresh)
 * instead of waiting for the next 30-second central poll. The backend writes into
 * the shared market_prices doc, so the live listener delivers the fresh values.
 * Server-side freshness gate + per-user cooldown keep API credits in check.
 * Fire-and-forget — returns the server JSON (or null on failure).
 */
export const refreshPricesNow = async (symbols) => {
  try {
    if (!symbols || symbols.length === 0) return null;
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    if (!auth.currentUser) return null;

    const token = await auth.currentUser.getIdToken();
    // Abort after 15s so a cold-start/hung connection never lingers. This is a
    // fire-and-forget call from the UI, so the live listener still delivers the
    // prices even if we abort waiting on the JSON response.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch('https://us-central1-investport-pro.cloudfunctions.net/refreshPricesNow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ symbols }),
        signal: controller.signal,
      });
      return await res.json();
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (err) {
    // Silent — refresh must never break the app
    console.warn('refreshPricesNow error:', err.message);
    return null;
  }
};

// DEPRECATED: in-app support tickets are no longer accepted. The app now
// routes all support requests to LINE @portpro (see SupportButton.js). The
// Firestore collection `support_tickets` is locked down via security rules.
// Kept as a stub so any stale imports do not crash; always returns false.
export const submitSupportTicket = async () => {
  console.warn('submitSupportTicket is deprecated — use the LINE support button instead.');
  return false;
};

// ── Feature Usage Tracking ──────────────────────────────────────────────────
// Fire-and-forget: silently increment a counter for each feature interaction.
// Data is stored at global_state/feature_usage  →  { [featureName]: number }
export const trackFeatureUsage = (featureName) => {
  try {
    const docRef = doc(db, 'global_state', 'feature_usage');
    setDoc(docRef, { [featureName]: increment(1) }, { merge: true })
      .catch(() => {}); // Silent — tracking must never break the app
  } catch (err) {
    // Silent
  }
};

export { analytics };
export default app;
